import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ymdToBr } from '../../relatorios/utils/relatoriosPeriodDateUtils';
import { TECH_STATUS_TYPES } from './monitoramentoStatusUtils';

const CAPTURE_CLASS = 'monitoramento-export-capture-mode';
const PDF_MARGIN_X = 3;
const PDF_MARGIN_Y = 3;

const HIDE_SELECTORS = [
  '.monitoramento-tv-exit-btn',
  '.monitoramento-tv-countdown',
  '.mon-tv-rot-overlay',
  '.monitoramento-externo-os-popover',
  '.monitoramento-externo-desloc-tip',
  '.monitoramento-modern-tooltip',
  '.board-clickup-tooltip',
  '.ec-actions-menu',
  '.equipment-consult-period-modal',
  '.monitoramento-panel-menu',
  '.monitoramento-new-panel-form'
];

function buildExportFilename(ext, prefix = 'monitoramento-externo') {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `${prefix}-${stamp}.${ext}`;
}

function getCaptureScale() {
  return Math.min(2, Math.max(1.25, window.devicePixelRatio || 1));
}

function hideChrome(root) {
  const hidden = [];
  if (!root) return hidden;
  HIDE_SELECTORS.forEach((selector) => {
    root.querySelectorAll(selector).forEach((el) => {
      hidden.push({ el, display: el.style.display });
      el.style.display = 'none';
    });
  });
  return hidden;
}

function restoreChrome(hidden) {
  hidden.forEach(({ el, display }) => {
    el.style.display = display;
  });
}

function formatStatusFilterValue(statusFilter, statusOptions) {
  if (!statusOptions?.length) return 'Todos';
  if (!statusFilter || statusFilter.length >= statusOptions.length) return 'Todos';
  const map = new Map(statusOptions.map((o) => [o.id, o.label]));
  return statusFilter.map((id) => map.get(id) || TECH_STATUS_TYPES[id]?.label || id).join(', ');
}

export function buildMonitoramentoExportMeta({
  panelTitle,
  selectedDate,
  statusFilter,
  statusOptions,
  isLive
}) {
  const filters = [
    { label: 'Painel', value: panelTitle || '—' },
    { label: 'Data', value: selectedDate ? ymdToBr(selectedDate) : '—' },
    { label: 'Status', value: formatStatusFilterValue(statusFilter, statusOptions) },
    { label: 'Atualização', value: isLive ? 'Tempo real' : 'Histórico' }
  ];

  return {
    title: 'Monitoramento — Equipe Externa',
    filters
  };
}

async function captureFullDashboard(dashboardEl) {
  if (!dashboardEl) throw new Error('Área do monitoramento indisponível.');

  const pageRoot = dashboardEl.closest('.monitoramento-page') || document.body;
  const tableScroll = dashboardEl.querySelector('.monitoramento-externo-table-scroll');
  const hidden = hideChrome(pageRoot);

  dashboardEl.classList.add(CAPTURE_CLASS);

  const stash = {
    dashboard: {
      overflow: dashboardEl.style.overflow,
      height: dashboardEl.style.height
    },
    tableScroll: tableScroll
      ? {
          overflow: tableScroll.style.overflow,
          height: tableScroll.style.height,
          maxHeight: tableScroll.style.maxHeight
        }
      : null
  };

  dashboardEl.style.overflow = 'visible';
  dashboardEl.style.height = 'auto';
  if (tableScroll) {
    tableScroll.style.overflow = 'visible';
    tableScroll.style.height = 'auto';
    tableScroll.style.maxHeight = 'none';
  }

  try {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const width = dashboardEl.scrollWidth;
    const height = dashboardEl.scrollHeight;

    return await html2canvas(dashboardEl, {
      backgroundColor: '#f8fafc',
      scale: getCaptureScale(),
      useCORS: true,
      logging: false,
      width,
      height,
      scrollX: 0,
      scrollY: 0,
      windowWidth: width,
      windowHeight: height,
      onclone: (doc) => {
        const cloned = doc.querySelector('.monitoramento-externo-dashboard');
        cloned?.classList.add(CAPTURE_CLASS);
        const clonedScroll = doc.querySelector('.monitoramento-externo-table-scroll');
        if (clonedScroll) {
          clonedScroll.style.overflow = 'visible';
          clonedScroll.style.height = 'auto';
          clonedScroll.style.maxHeight = 'none';
        }
        HIDE_SELECTORS.forEach((selector) => {
          doc.querySelectorAll(selector).forEach((el) => {
            el.style.display = 'none';
          });
        });
      }
    });
  } finally {
    dashboardEl.style.overflow = stash.dashboard.overflow;
    dashboardEl.style.height = stash.dashboard.height;
    if (tableScroll && stash.tableScroll) {
      tableScroll.style.overflow = stash.tableScroll.overflow;
      tableScroll.style.height = stash.tableScroll.height;
      tableScroll.style.maxHeight = stash.tableScroll.maxHeight;
    }
    dashboardEl.classList.remove(CAPTURE_CLASS);
    restoreChrome(hidden);
  }
}

async function captureVisibleViewport(contentEl) {
  if (!contentEl) throw new Error('Área do monitoramento indisponível.');

  const pageRoot = contentEl.closest('.monitoramento-page') || document.body;
  const hidden = hideChrome(pageRoot);

  const width = contentEl.clientWidth;
  const height = contentEl.clientHeight;
  if (width < 1 || height < 1) {
    restoreChrome(hidden);
    throw new Error('Área do monitoramento indisponível.');
  }

  try {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    return await html2canvas(contentEl, {
      backgroundColor: '#f8fafc',
      scale: getCaptureScale(),
      useCORS: true,
      allowTaint: true,
      logging: false,
      width,
      height,
      x: contentEl.scrollLeft,
      y: contentEl.scrollTop,
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      onclone: (doc) => {
        HIDE_SELECTORS.forEach((selector) => {
          doc.querySelectorAll(selector).forEach((el) => {
            el.style.display = 'none';
          });
        });
      }
    });
  } finally {
    restoreChrome(hidden);
  }
}

function drawFilterNavbar(pdf, filters, x, y, width) {
  const segments = filters.map((f) => `${f.label}: ${f.value}`);
  const navbarText = segments.join('   |   ');

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6.8);
  const lines = pdf.splitTextToSize(navbarText, width - 6);
  const lineCount = Math.min(lines.length, 2);
  const barH = 4.5 + lineCount * 3.4;

  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.2);
  pdf.roundedRect(x, y, width, barH, 1.4, 1.4, 'FD');

  pdf.setTextColor(51, 65, 85);
  for (let i = 0; i < lineCount; i += 1) {
    pdf.text(lines[i], x + 3, y + 4.2 + i * 3.4);
  }
  pdf.setTextColor(0, 0, 0);

  return y + barH;
}

function sliceCanvas(canvas, srcY, srcH) {
  const slice = document.createElement('canvas');
  slice.width = canvas.width;
  slice.height = srcH;
  const ctx = slice.getContext('2d');
  ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
  return slice;
}

function addFullWidthImage(pdf, canvas, x, y, width, maxHeight, pageH) {
  const fullHeight = (canvas.height / canvas.width) * width;
  if (fullHeight <= maxHeight) {
    const imgData = canvas.toDataURL('image/jpeg', 0.88);
    pdf.addImage(imgData, 'JPEG', x, y, width, fullHeight, undefined, 'FAST');
    return;
  }

  const sliceHeightPx = Math.floor((maxHeight / fullHeight) * canvas.height);
  let srcY = 0;
  let cursorY = y;
  let remainingPx = canvas.height;

  while (remainingPx > 0) {
    const currentSlicePx = Math.min(sliceHeightPx, remainingPx);
    const sliceH = (currentSlicePx / canvas.height) * fullHeight;
    const slice = sliceCanvas(canvas, srcY, currentSlicePx);
    const imgData = slice.toDataURL('image/jpeg', 0.88);
    pdf.addImage(imgData, 'JPEG', x, cursorY, width, sliceH, undefined, 'FAST');

    remainingPx -= currentSlicePx;
    srcY += currentSlicePx;

    if (remainingPx > 0) {
      pdf.addPage();
      cursorY = PDF_MARGIN_Y;
    }
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function exportMonitoramentoPdf(dashboardEl, exportMeta) {
  const canvas = await captureFullDashboard(dashboardEl);
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - PDF_MARGIN_X * 2;

  let y = PDF_MARGIN_Y;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text(exportMeta?.title || 'Monitoramento — Equipe Externa', PDF_MARGIN_X, y + 4);
  y += 8;

  y = drawFilterNavbar(pdf, exportMeta?.filters || [], PDF_MARGIN_X, y, contentW) + 2;

  const maxImgH = pageH - y - PDF_MARGIN_Y;
  addFullWidthImage(pdf, canvas, PDF_MARGIN_X, y, contentW, maxImgH, pageH);
  const filePrefix = exportMeta?.filePrefix || 'monitoramento-externo';
  pdf.save(buildExportFilename('pdf', filePrefix));
}

export async function exportMonitoramentoPng(contentEl, options = {}) {
  const canvas = await captureVisibleViewport(contentEl);
  const blob = await new Promise((resolve) => {
    canvas.toBlob((result) => resolve(result), 'image/png');
  });
  if (!blob) throw new Error('Não foi possível gerar a imagem.');
  downloadBlob(blob, buildExportFilename('png', options.filePrefix || 'monitoramento-externo'));
}

/**
 * PDF/PNG do LiveMap: captura o shell do mapa (tiles + pins + sidebar).
 */
export async function exportMapAlivePdf(dashboardEl, exportMeta) {
  if (!dashboardEl) throw new Error('Área do LiveMap indisponível.');
  const pageRoot = dashboardEl.closest('.monitoramento-page') || document.body;
  const hidden = hideChrome(pageRoot);
  try {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const canvas = await html2canvas(dashboardEl, {
      backgroundColor: '#f8fafc',
      scale: getCaptureScale(),
      useCORS: true,
      allowTaint: true,
      logging: false,
      onclone: (doc) => {
        HIDE_SELECTORS.forEach((selector) => {
          doc.querySelectorAll(selector).forEach((el) => {
            el.style.display = 'none';
          });
        });
      }
    });
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const contentW = pageW - PDF_MARGIN_X * 2;
    let y = PDF_MARGIN_Y;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(exportMeta?.title || 'Monitoramento — LiveMap', PDF_MARGIN_X, y + 4);
    y += 8;
    y = drawFilterNavbar(pdf, exportMeta?.filters || [], PDF_MARGIN_X, y, contentW) + 2;
    const maxImgH = pageH - y - PDF_MARGIN_Y;
    addFullWidthImage(pdf, canvas, PDF_MARGIN_X, y, contentW, maxImgH, pageH);
    pdf.save(buildExportFilename('pdf', 'monitoramento-livemap'));
  } finally {
    restoreChrome(hidden);
  }
}

export async function exportMapAlivePng(dashboardEl) {
  if (!dashboardEl) throw new Error('Área do LiveMap indisponível.');
  const pageRoot = dashboardEl.closest('.monitoramento-page') || document.body;
  const hidden = hideChrome(pageRoot);
  try {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const canvas = await html2canvas(dashboardEl, {
      backgroundColor: '#f8fafc',
      scale: getCaptureScale(),
      useCORS: true,
      allowTaint: true,
      logging: false,
      onclone: (doc) => {
        HIDE_SELECTORS.forEach((selector) => {
          doc.querySelectorAll(selector).forEach((el) => {
            el.style.display = 'none';
          });
        });
      }
    });
    const blob = await new Promise((resolve) => {
      canvas.toBlob((result) => resolve(result), 'image/png');
    });
    if (!blob) throw new Error('Não foi possível gerar a imagem.');
    downloadBlob(blob, buildExportFilename('png', 'monitoramento-livemap'));
  } finally {
    restoreChrome(hidden);
  }
}
